import * as React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { find, union, filter } from 'lodash';
import { animateScroll as scroll } from 'react-scroll';
import routes, { routeFor } from 'helpers/routes';
import { ProductType, ProductCategoryType } from 'types';
import { theme, media } from 'styles';
import { sizes } from 'styles/media';
import bannerImage from 'images/MT-ServicesHeader.png';
import { Breadcrumbs, Container, TypingHero, SeoTags } from 'components/shared';
import { Sidebar, List } from './components';

const Wrapper = styled.div`
  background-color: ${theme.colors.blueIce};

  ${media.tablet`
    background-position: top 0 right 0;
    background-size: auto;
  `};
`;

const Hero = styled.div`
  display: block;
  position: relative;
  background-size: contain;
  background-position: top right;
  background-repeat: no-repeat;
  padding-bottom: 60px;
  padding-top: 40px;

  ${media.tablet`
    background-image: url('${props => props.src}');
    padding-bottom: 220px;
    padding-top: 120px;
  `};
`;

const Main = styled(Container)`
  display: flex;
  flex-direction: column;

  ${media.desktop`
    flex-direction: row;
  `};
`;

const ProductsBreadcrumbs = styled.div`
  margin-bottom: 20px;
`;

const propTypes = {
  products: PropTypes.arrayOf(ProductType).isRequired,
  productCategories: PropTypes.arrayOf(ProductCategoryType).isRequired,
  selectedCategoryId: PropTypes.string,
  seoTitle: PropTypes.string,
  seoKeywords: PropTypes.string,
  seoDescription: PropTypes.string,
};

const defaultProps = {
  selectedCategoryId: undefined,
  seoTitle: 'Pay-as-you-go Marketing Services | MarketTap',
  seoKeywords: undefined,
  seoDescription:
    'We provide affordable marketing services for small businesses looking to take their marketing to the next level. Why get tied into a contract when you can pay-as-you-go?',
};

class ProductsPage extends React.Component {
  state = {
    selectedCategoryId: this.props.selectedCategoryId,
    searchTerm: '',
    seoTitle: this.props.seoTitle,
    seoKeywords: this.props.seoKeywords,
    seoDescription: this.props.seoDescription,
  };

  getBreadcrumbs = () => {
    const { selectedCategoryId } = this.state;
    const initial = [
      {
        name: 'Home',
        link: `${routes.home.index}`,
      },
      {
        name: 'Services',
        link: `${routes.products.index}`,
      },
    ];

    if (selectedCategoryId) {
      const category = find(
        this.props.productCategories,
        cat => cat.id === selectedCategoryId,
      );
      const { name, slug } = category;
      return union(initial, [
        { name, link: routeFor(routes.products.category, slug) },
      ]);
    }
    return initial;
  };

  getProducts = () => {
    const { products } = this.props;
    const { searchTerm } = this.state;
    const filteredProducts = filter(products, product => (
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.productCategories[0].name
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ));
    return filteredProducts;
  };

  updateSearch = ({ target }) => {
    this.setState({ searchTerm: target.value });
  };

  selectCategory = slug => {
    const selectedCategory =
      filter(
        this.props.productCategories,
        category => category.slug === slug,
      )[0] || {};
    this.setState({
      selectedCategoryId: selectedCategory.id,
      seoTitle: selectedCategory.seoTitle,
      seoKeywords: selectedCategory.seoKeywords,
      seoDescription: selectedCategory.seoDescription,
    });
    if (window.history && window.history.pushState) {
      const url = slug
        ? routeFor(routes.products.category, slug)
        : routes.products.index;
      window.history.pushState(
        { selectedCategoryId: selectedCategory.id },
        '',
        url,
      );
    }
    if (window.innerWidth < sizes.desktop) scroll.scrollTo(750);
  };

  render() {
    const { productCategories } = this.props;
    const products = this.getProducts();
    const selectedCategory = find(
      this.props.productCategories,
      category => category.id === this.state.selectedCategoryId,
    ) || { productIds: products.map(p => p.id) };
    const {
      selectedCategoryId,
      searchTerm,
      seoTitle,
      seoKeywords,
      seoDescription,
    } = this.state;
    const breadcrumbs = this.getBreadcrumbs();

    return (
      <Wrapper>
        <SeoTags
          title={seoTitle}
          keywords={seoKeywords}
          description={seoDescription}
        />

        <Hero src={bannerImage}>
          <Container>
            <ProductsBreadcrumbs>
              <Breadcrumbs left breadcrumbs={breadcrumbs} />
            </ProductsBreadcrumbs>
            <TypingHero
              heading="Helping you"
              lines={[
                'Create a digital strategy',
                'Reach your customers',
                'Build brand awareness',
                'Increase sales',
                'Create content',
                'Get results',
                'Stay ahead of competitors',
              ]}
            />
          </Container>
        </Hero>

        <Main>
          <Sidebar
            selectCategory={this.selectCategory}
            productCategories={productCategories}
            selectedCategoryId={selectedCategoryId}
            searchTerm={searchTerm}
            updateSearch={this.updateSearch}
          />

          <List products={products} selectedCategory={selectedCategory} />
        </Main>
      </Wrapper>
    );
  }
}

ProductsPage.propTypes = propTypes;
ProductsPage.defaultProps = defaultProps;

export default ProductsPage;



// WEBPACK FOOTER //
// ./src/components/ProductsPage/ProductsPage.jsx